/* eslint-disable prefer-promise-reject-errors */
import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios'
import qs from 'qs'
// @ts-ignore
import fileSaver from 'file-saver'
import { CATEGORY } from 'features/dashboardInfo/constants'
import { FetchQueryOptions } from '@tanstack/react-query'
import { ScheduledReport } from 'features/scheduledReport/types'
import { TimelineAnnotation } from 'schemas/TimelineAnnotationSchema'
import {
  checkForJWTToken,
  getJWTToken,
  requestJWTTokenWithTimeout,
  waitForJWTToken,
  getIsRefreshingToken,
  setIsRefreshingToken,
  addToFailedJWTTokenQueue,
  processJWTTokenQueue
} from 'utils/jwtUtil'
import { getBaseUrl } from 'utils/urlUtil'
import getFileNameFromHeader from './utils/exportUtil'
import queryClient from './app/queryClient'

export async function retrieveJWTToken(): Promise<string | null> {
  if (process.env.REACT_APP_JWT_ENABLED === 'true') {
    try {
      if (!checkForJWTToken()) {
        console.log('No valid token found, requesting new token')
        // Request a new token
        await requestJWTTokenWithTimeout()
        // Wait for the token to be set by the message handler
        await waitForJWTToken()
      }
      return getJWTToken()
    } catch (error) {
      console.error('Error getting JWT token:', error)
      return null
    }
  }
  return null
}

const API_BASE_URL = `${getBaseUrl()}/cb/api/2/`

const request = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/x-www-form-urlencoded',
    'X-CLIENT-VERSION': 'V4'
  }
})

request.interceptors.request.use(
  async (config) => {
    try {
      const token = await retrieveJWTToken()
      if (token) {
        // eslint-disable-next-line no-param-reassign
        config.headers.Authorization = `Bearer ${token}`
      } else if (process.env.NODE_ENV === 'development') {
        // Only use basic auth in development environment
        const devUser = process.env.REACT_APP_DEV_USER
        const devPassword = process.env.REACT_APP_DEV_PASSWORD

        if (devUser && devPassword) {
          // eslint-disable-next-line no-param-reassign
          config.auth = {
            username: devUser,
            password: devPassword
          }
        } else {
          console.warn('Development credentials not configured. Some API calls may fail.')
        }
      }
    } catch (error) {
      console.error('Error in request interceptor:', error)
    }
    return config
  },
  (error) => Promise.reject(error)
)

request.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config

    if (error.response?.status === 401 && !originalRequest.retry) {
      if (getIsRefreshingToken()) {
        return new Promise((resolve, reject) => {
          addToFailedJWTTokenQueue(resolve, reject)
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`
            return request(originalRequest)
          })
          .catch((err) => Promise.reject(err))
      }

      originalRequest.retry = true
      setIsRefreshingToken(true)

      try {
        const refreshPromise = requestJWTTokenWithTimeout()

        await refreshPromise
        // Wait for the token to be set by the message handler
        await waitForJWTToken()

        const token = getJWTToken()
        if (!token) {
          throw new Error('Failed to obtain new token')
        }

        processJWTTokenQueue(null, token)

        originalRequest.headers.Authorization = `Bearer ${token}`
        return await request(originalRequest)
      } catch (refreshError) {
        console.error('Failed to refresh token:', refreshError)

        processJWTTokenQueue(refreshError)

        return await Promise.reject(refreshError)
      } finally {
        setIsRefreshingToken(false)
      }
    }

    return Promise.reject(error)
  }
)

const anonymousRequest = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'X-CLIENT-VERSION': 'V4'
  }
})

export interface FetchErrorResponse {
  error?: string
  status?: number
  info?: string[]
}

export type FetchError = AxiosError<FetchErrorResponse>

export async function getCustomVizWidgetData(widgetId: number) {
  const dataStr = qs.stringify({})
  const response = await queryClient.fetchQuery([`/widget-data/${widgetId}`, dataStr], () =>
    request.post(`/widget-data/${widgetId}`, dataStr)
  )
  return response
}

export async function getRecentRecords() {
  const dataStr = qs.stringify({
    needChannelLevelDetection: true,
    searchInfo: false
  })
  const response = await queryClient.fetchQuery(['/getRecentRecords', dataStr], () =>
    request.post('/getRecentRecords', dataStr)
  )
  return response
}

export async function getDashboardInfos(category?: string, options?: FetchQueryOptions) {
  let params: { type?: string; scope?: string } | undefined
  switch (category) {
    case CATEGORY.allDashboards:
      params = { type: 'DASHBOARD' }
      break
    case CATEGORY.companyDashboards:
      params = { type: 'DASHBOARD', scope: 'SHARED' }
      break
    case CATEGORY.myDashboards:
      params = { type: 'DASHBOARD', scope: 'OWNED' }
      break
    case CATEGORY.myTemplates:
      params = { type: 'TEMPLATE', scope: 'OWNED' }
      break
    case CATEGORY.companyTemplates:
      params = { type: 'TEMPLATE', scope: 'SHARED' }
      break
    case CATEGORY.recentlyViewedDashboards:
    default:
      params = undefined
      break
  }

  const response = (await queryClient.fetchQuery(
    ['/dashboards/info', category],
    () =>
      request.get('/dashboards/info', {
        params
      }),
    options
  )) as AxiosResponse<any>
  return response
}

export async function getRecentViewedDashboardInfos(options?: FetchQueryOptions) {
  const response = (await queryClient.fetchQuery(
    ['/dashboards/recently-viewed'],
    () => request.get('/dashboards/recently-viewed'),
    options
  )) as AxiosResponse<any>
  return response
}

export async function getTopicInfos() {
  const dataStr = qs.stringify({
    searchInfo: false
  })
  const response = await queryClient.fetchQuery(['/getAllAccessibleTopicInfos', dataStr], () =>
    request.post('/getAllAccessibleTopicInfos', dataStr)
  )
  return response
}

export async function getPageConfig() {
  const dataStr = qs.stringify({
    page: 'CB_WEB'
  })
  const response = await queryClient.fetchQuery(['/getPageConfig', dataStr], () =>
    request.post('/getPageConfig', dataStr)
  )
  return response
}

export async function retrieveEmojis() {
  const dataStr = qs.stringify({})
  const response = await queryClient.fetchQuery(['/retrieveEmojis', dataStr], () =>
    request.post('/retrieveEmojis', dataStr)
  )
  return response
}

export async function getDashboard(dashboardId: number) {
  const response = await queryClient.fetchQuery(['/dashboards', dashboardId], () =>
    request.get(`/dashboards/${dashboardId}`)
  )

  return response
}

export async function updateDashboardRecentView(dashboardId: number) {
  const dataStr = qs.stringify({
    dashboardId
  })
  const response = await queryClient.fetchQuery(['/setLastRunDashboard', dashboardId], () =>
    request.post('/setLastRunDashboard', dataStr)
  )
  // * TODO: change this to restful API
  return response
}

export async function runSearch(
  dashboardId: number,
  dashboardName: string,
  spec: any,
  widgetId?: number | string,
  timestamp?: number,
  requestConfig?: AxiosRequestConfig,
  llmWordCloud?: boolean
) {
  const dataStr = qs.stringify({
    dashboardId,
    dashboardName,
    spec: JSON.stringify(spec),
    searchInfo: false, // TODO: originally from window.searchInfo
    // monitor-app RunServicesController reads llm_wordcloud as a @RequestParam
    // and routes Word Cloud requests through LlmWordCloudService when true.
    // Omit the key entirely when not opted in so existing flows are byte-identical.
    ...(llmWordCloud ? { llm_wordcloud: true } : {})
  })
  const response = await queryClient.fetchQuery(
    ['/runSearches', timestamp, widgetId, dataStr, requestConfig],
    () => request.post('/runSearches', dataStr, requestConfig)
  )
  return response
}

export async function exportPdf(
  html: string,
  filename: string,
  windowWidth: number,
  windowHeight: number,
  pageHeight?: number
) {
  let data = {
    html: JSON.stringify(html),
    filename: JSON.stringify(filename),
    windowWidth: windowWidth.toString(),
    windowHeight: windowHeight.toString()
  } as any

  if (pageHeight) {
    data = {
      ...data,
      pageHeight: pageHeight.toString()
    }
  }

  const dataStr = qs.stringify(data)

  const response = await queryClient.fetchQuery(['/generateV4Pdf', dataStr], () =>
    request.post('/generateV4Pdf', dataStr, {
      responseType: 'blob',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        Accept:
          'text/html,application/xhtml+xml,application/xml;q=0.9,image/avif,image/webp,image/apng,*/*;q=0.8,application/signed-exchange;v=b3;q=0.7'
      }
    })
  )
  const fileName = getFileNameFromHeader(response.headers['content-disposition'])
  const blob = new Blob([response.data], { type: 'application/pdf' })
  fileSaver.saveAs(blob, fileName || 'dashboard_export.pdf')
}

export async function getLiveReportId(dashboardId: number) {
  const dataStr = qs.stringify({
    dashboardId,
    refresh: false
  })
  const response = await queryClient.fetchQuery(
    ['/getLiveReportId', dataStr],
    () => request.post('/getLiveReportId', dataStr),
    // override staleTime to 0 to always get the new reportId
    {
      staleTime: 0
    }
  )
  return response
}

export async function getLiveReportInfo(dashboardId: number, clientVersion?: string) {
  const headers = {
    'Content-Type': 'application/x-www-form-urlencoded',
    'X-CLIENT-VERSION': clientVersion ? 'V3' : 'V4'
  }

  const dataStr = qs.stringify({
    dashboardId
  })
  const response = await queryClient.fetchQuery(
    ['/getLiveReportInfo', dataStr],
    () => request.post('/getLiveReportInfo', dataStr, { headers }),
    // override staleTime to 0 to always get the new reportInfo
    {
      staleTime: 0
    }
  )

  return response
}

export async function getSuggestedUserList(accountId: number) {
  const dataStr = qs.stringify({
    accountId,
    searchInfo: false
  })
  const response = await queryClient.fetchQuery(['/getSuggestedAccountUsers', dataStr], () =>
    request.post('/getSuggestedAccountUsers', dataStr)
  )
  return response
}

export async function updateLiveReportStatus(
  dashboardId: number,
  liveReportInfo: {
    liveReportEnabled: boolean
    anonymous: boolean
    liveReportId: string
    liveReportKey: string
    liveReportSubscribers: Array<{ id: number } | { id: number; name: string }>
  }
) {
  const dataStr = qs.stringify({
    dashboardId,
    liveReportInfo: JSON.stringify(liveReportInfo)
  })
  const response = await queryClient.fetchQuery(
    ['/updateLiveReportStatus', dataStr],
    () => request.post('/updateLiveReportStatus', dataStr),
    {
      staleTime: 0
    }
  )

  return response
}

export async function getDashboardAlerts(dashboardId: string) {
  const response = await queryClient.fetchQuery(
    ['/dashboards/scheduled-report', dashboardId],
    () => request.get(`/dashboards/${dashboardId}/scheduled-report`),
    {
      staleTime: 0
    }
  )
  return response
}

export async function subscribeDashboardAlerts(dashboardId: number) {
  const dataStr = qs.stringify({})
  const response = await request.post(
    `/dashboards/${dashboardId}/subscribe-scheduled-report`,
    dataStr
  )
  return response
}

export async function unsubscribeDashboardAlerts(dashboardId: number) {
  const response = await request.delete(`/dashboards/${dashboardId}/subscribe-scheduled-report`)
  return response
}

export async function createUpdateScheduledReport(
  dashboardId: number,
  reportConfig: ScheduledReport
) {
  const response = await request.post(`/dashboards/${dashboardId}/scheduled-report`, reportConfig, {
    headers: {
      'Content-Type': 'application/json'
    }
  })
  return response
}

export async function deleteScheduledReport(dashboardId: number) {
  const response = await request.delete(`/dashboards/${dashboardId}/scheduled-report`)
  return response
}

export async function getDashboardNameBySubscriptionUuId(subscriptionUuId: number | string) {
  const response = await queryClient.fetchQuery(
    ['/subscription/dashboard-name', subscriptionUuId],
    () =>
      anonymousRequest.get(`/subscription/dashboard-name`, {
        params: { uuid: `"${subscriptionUuId}"` }
        // * double quote caused by v3 service has modified JsonListConverter or JsonConverterFactory
        // * to resolve this, we need to refactor v3 service or create a new micro service
      })
  )
  return response
}

export async function unsubscribeDashboardBySubscriptionUuId(subscriptionUuId: number | string) {
  const response = await anonymousRequest.delete(`/subscription`, {
    data: subscriptionUuId
  })
  return response
}

export async function updateAnnotation(annotation: TimelineAnnotation) {
  const dataStr = qs.stringify({
    timelineAnnotation: JSON.stringify(annotation)
  })
  const response = await request.post('/updateTimelineAnnotation', dataStr)
  return response
}

export async function deleteAnnotation(annotation: TimelineAnnotation) {
  const dataStr = qs.stringify({
    timelineAnnotation: JSON.stringify(annotation)
  })
  const response = await request.post('/delTimelineAnnotation', dataStr)
  return response
}

export async function addAnnotation(annotation: TimelineAnnotation) {
  const dataStr = qs.stringify({
    timelineAnnotation: JSON.stringify(annotation)
  })
  const response = await request.post('/addTimelineAnnotation', dataStr)
  return response
}

export async function getAnnotations(topicId: number, options?: FetchQueryOptions) {
  const dataStr = qs.stringify({
    groupId: topicId
  })
  const response = (await queryClient.fetchQuery(
    ['/getTimelineAnnotationGroup', dataStr],
    () => request.post('/getTimelineAnnotationGroup', dataStr),
    options
  )) as AxiosResponse<any>
  return response
}

export async function deleteDashboard(dashboardId: number) {
  const response = await request.delete(`/dashboards/${dashboardId}`)
  return response
}

export async function copyDashboard(dashboardId: number, dashboardName: string) {
  const response = await request.post<{ data: number }>(
    `/dashboards/${dashboardId}/copy`,
    {
      dashboardName
    },
    {
      headers: {
        'Content-Type': 'application/json'
      }
    }
  )
  return response.data
}
